import React, { useEffect, useState } from 'react';
import { AlertCircle, ArrowLeft, Loader2, Save } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { CUSTOMER_TYPE_LABELS, DEFAULT_COUNTRY, getAdminUser, ROLE_LABELS, updateAdminUser } from '../../config';
import { useAuth } from '../../context/AuthContext';

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('users:update');
  const [user, setUser] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const data = await getAdminUser(id);
        if (!active) return;
        const currentUser = data.user;
        setUser(currentUser);
        setForm({
          first_name: currentUser.first_name || '',
          last_name: currentUser.last_name || '',
          phone: currentUser.phone || '',
          address: currentUser.address || '',
          postal_code: currentUser.postal_code || '',
          city: currentUser.city || '',
          country: currentUser.country || DEFAULT_COUNTRY,
          customer_type: currentUser.customer_type || 'particulier',
          company_name: currentUser.company_name || '',
          siret: currentUser.siret || '',
          role: currentUser.role === 'superadmin' ? 'admin_boutique' : currentUser.role,
          is_active: currentUser.is_active,
        });
      } catch (err) {
        if (active) setError(err.message || 'Impossible de charger cette fiche utilisateur.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [id]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'customer_type' && value === 'particulier'
        ? { company_name: '', siret: '' }
        : {}),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canEdit) return;

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const data = await updateAdminUser(id, form);
      setUser(data.user);
      setMessage('Utilisateur mis a jour.');
    } catch (err) {
      setError(err.message || 'Impossible de mettre a jour cet utilisateur.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Fiche utilisateur">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-secondary animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!user || !form) {
    return (
      <AdminLayout title="Fiche utilisateur">
        <div className="bg-white rounded-sm border border-slate-200 p-8 text-center text-slate-600">{error || 'Utilisateur introuvable.'}</div>
      </AdminLayout>
    );
  }

  const showBusinessFields = form.customer_type === 'professionnel';
  const isSuperadmin = user.role === 'superadmin';

  return (
    <AdminLayout title="Fiche utilisateur">
      <div className="max-w-5xl space-y-4">
        <Link to="/my/utilisateurs" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-secondary transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Retour aux utilisateurs
        </Link>

        <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-4">
          <form onSubmit={handleSubmit} className="bg-white rounded-sm border border-slate-200 p-5 sm:p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="space-y-1.5">
                <span className="text-xs text-slate-600">Prenom</span>
                <input
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  disabled={!canEdit}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                  required
                />
              </label>
              <label className="space-y-1.5">
                <span className="text-xs text-slate-600">Nom</span>
                <input
                  type="text"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  disabled={!canEdit}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                  required
                />
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="space-y-1.5">
                <span className="text-xs text-slate-600">Email Google</span>
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="w-full bg-slate-100 border border-slate-200 rounded-sm px-4 py-3 text-sm text-slate-500 cursor-not-allowed"
                />
              </label>
              <label className="space-y-1.5">
                <span className="text-xs text-slate-600">Telephone</span>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  disabled={!canEdit}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                  required
                />
              </label>
            </div>

            <label className="space-y-1.5 block">
              <span className="text-xs text-slate-600">Adresse</span>
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                disabled={!canEdit}
                className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                required
              />
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="space-y-1.5">
                <span className="text-xs text-slate-600">Code postal</span>
                <input
                  type="text"
                  name="postal_code"
                  value={form.postal_code}
                  onChange={handleChange}
                  disabled={!canEdit}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                  required
                />
              </label>
              <label className="space-y-1.5 sm:col-span-2">
                <span className="text-xs text-slate-600">Ville</span>
                <input
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  disabled={!canEdit}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                  required
                />
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="space-y-1.5">
                <span className="text-xs text-slate-600">Pays</span>
                <input
                  type="text"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  disabled={!canEdit}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                  required
                />
              </label>
              <label className="space-y-1.5">
                <span className="text-xs text-slate-600">Type de client</span>
                <select
                  name="customer_type"
                  value={form.customer_type}
                  onChange={handleChange}
                  disabled={!canEdit}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                >
                  {Object.entries(CUSTOMER_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {showBusinessFields && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="space-y-1.5">
                  <span className="text-xs text-slate-600">Raison sociale</span>
                  <input
                    type="text"
                    name="company_name"
                    value={form.company_name}
                    onChange={handleChange}
                    disabled={!canEdit}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                    required={showBusinessFields}
                  />
                </label>
                <label className="space-y-1.5">
                  <span className="text-xs text-slate-600">SIRET</span>
                  <input
                    type="text"
                    name="siret"
                    value={form.siret}
                    onChange={handleChange}
                    disabled={!canEdit}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                    required={showBusinessFields}
                  />
                </label>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="space-y-1.5">
                <span className="text-xs text-slate-600">Role</span>
                <select
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                  disabled={!canEdit || isSuperadmin}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none disabled:opacity-60"
                >
                  <option value="client">Client</option>
                  <option value="gestionnaire">Gestionnaire</option>
                  <option value="admin_boutique">Admin boutique</option>
                </select>
              </label>

              <label className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 mt-6">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={Boolean(form.is_active)}
                  onChange={handleChange}
                  disabled={!canEdit || isSuperadmin}
                  className="rounded bg-slate-50 border-slate-300 text-secondary focus:ring-secondary"
                />
                <span className="text-sm text-primary">Compte actif</span>
              </label>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-sm p-3 text-sm text-rose-600">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {message && <div className="text-sm text-emerald-600">{message}</div>}

            {canEdit && (
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-5 py-2.5 text-sm transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer
              </button>
            )}
          </form>

          <div className="space-y-4">
            <div className="bg-white rounded-sm border border-slate-200 p-5 space-y-3">
              <h2 className="font-heading text-lg font-semibold text-primary">Activite</h2>
              <div className="text-sm text-slate-700 space-y-2">
                <p>Derniere reservation: <span className="text-slate-600">{formatDate(user.last_reservation_at)}</span></p>
                <p>Derniere commande: <span className="text-slate-600">{formatDate(user.last_order_at)}</span></p>
                <p>Derniere activite: <span className="text-slate-600">{formatDate(user.last_activity_at)}</span></p>
              </div>
            </div>

            <div className="bg-white rounded-sm border border-slate-200 p-5 space-y-3">
              <h2 className="font-heading text-lg font-semibold text-primary">Meta</h2>
              <div className="text-sm text-slate-700 space-y-2">
                <p>Creation: <span className="text-slate-600">{formatDate(user.created_at)}</span></p>
                <p>Derniere mise a jour: <span className="text-slate-600">{formatDate(user.updated_at)}</span></p>
                <p>Derniere connexion: <span className="text-slate-600">{formatDate(user.last_login_at)}</span></p>
                <p>Profil complet: <span className="text-slate-600">{user.profile_completed ? 'Oui' : 'Non'}</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
